import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, 
  MenuItem, 
  Avatar, 
  Divider, 
  ListItemIcon, 
  Box,
  Typography,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import { 
  SettingsOutlined, 
  FileOpenOutlined, 
  QuestionMark, 
  LogoutOutlined, 
  KeyboardArrowRightOutlined 
} from '@mui/icons-material';
import { logout } from 'store/slices/user.slice';
import { useDispatch } from 'react-redux';
import { useLogoutMutation } from 'store/api/auth.apislice';
import { persistor } from 'store/index';
import { clearTokens } from 'utils/auth.utils';
import Modal from 'components/mui/Modal';
import Button from 'components/mui/Button';
import { typography } from 'theme';
// import { getInitials } from 'utils/string.utils'; // Not used in this component

// Component scope - Constants and static data
const MENU_ITEMS = [
  {
    id: 'reports',
    label: 'Reports',
    icon: <FileOpenOutlined fontSize="small" />,
    route: '/reports',
    color: 'primary',
  },
  {
    id: 'help',
    label: 'Help Center',
    icon: <QuestionMark fontSize="small" />,
    route: '/help-center',
    color: 'primary',
  },
  {
    id: 'logout',
    label: 'Logout',
    icon: <LogoutOutlined fontSize="small" />,
    color: 'error',
  },
];

/**
 * Profile Menu Component - User profile dropdown menu
 * @component
 * @param {Object} props - Component props
 * @returns {React.ReactElement} ProfileMenuComponent
 */
const ProfileMenuComponent = memo(({ 
  userName, 
  userEmail, 
  anchorEl, 
  open, 
  onClose,
  onMenuItemClick,
  restrictedMenu = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const displayName = userName ?? 'User';
  const displayEmail = userEmail ?? '';
  
  // States, refs, selectors, RTK Query hooks
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutMutation, { isLoading: isLoggingOut }] = useLogoutMutation();

  // Other functions and logic
  const isItemDisabled = useCallback((itemId) => {
    if (restrictedMenu !== true) return false;
    return itemId !== 'logout';
  }, [restrictedMenu]);

  const handleMenuItemClick = (itemId) => {
    if (!itemId || isItemDisabled(itemId) === true) return;

    const menuItem = (MENU_ITEMS ?? []).find((item) => item?.id === itemId);
    
    if (itemId === 'logout') {
      setShowLogoutConfirm(true);
    } else if (menuItem?.route) {
      navigate(menuItem?.route ?? '/');
      onClose?.();
    }
    
    onMenuItemClick?.(itemId);
  };

  const handleProfileClick = () => {
    if (restrictedMenu === true) return;
    navigate('/profile');
    onClose?.();
  };

  const handleCancelLogout = useCallback(() => {
    setShowLogoutConfirm(false);
  }, []);

  const handleConfirmLogout = useCallback(async () => {
    try {
      // Call logout API (fire and forget - proceed even if it fails)
      try {
        const logoutResult = logoutMutation?.();
        if (logoutResult?.unwrap) {
          await logoutResult.unwrap();
        }
      } catch (apiError) {
        console.warn(
          'Logout API call failed, proceeding with local cleanup:',
          apiError?.data?.message ?? apiError?.message ?? apiError,
        );
      }

      await persistor?.purge?.();

      // Clear tokens from localStorage
      clearTokens();

      // Clear Redux state
      dispatch(logout());

      // Close dialogs
      setShowLogoutConfirm(false);
      onClose?.();

      // Navigate to login page
      navigate('/login');
    } catch (error) {
      // Even if something fails, ensure cleanup happens
      console.error('Error during logout:', error);
      
      // Force cleanup
      try {
        await persistor?.purge?.();
      } catch (purgeError) {
        console.error(
          'Error purging persistor:',
          purgeError?.message ?? purgeError,
        );
      }
      
      clearTokens();
      dispatch(logout());
      
      setShowLogoutConfirm(false);
      onClose?.();
      navigate('/login');
    }
  }, [logoutMutation, dispatch, navigate, onClose]);

  const getAvatarBackgroundColor = (color) => {
    switch (color) {
      case 'error':
        return 'rgba(235, 84, 86, 0.16)';
      default:
        return 'rgba(70, 168, 220, 0.16)';
    }
  };

  const getIconColor = (color) => {
    switch (color) {
      case 'error':
        return theme.palette?.error?.main;
      default:
        return theme.palette.custom?.icon?.primary;
    }
  };

  // Return/rendering
  return (
    <>
      <Menu
        anchorEl={anchorEl ?? null}
        id="account-menu"
        open={open === true}
        onClose={() => onClose?.()}
        onClick={() => onClose?.()}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              borderRadius: 1.25,
              width: '416px',
              backgroundColor: theme.palette.custom?.background?.popup,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info MenuItem */}
        <MenuItem
          onClick={handleProfileClick}
          disabled={restrictedMenu === true}
          sx={{
            cursor: restrictedMenu === true ? 'default' : 'pointer',
            opacity: restrictedMenu === true ? 0.5 : 1,
          }}
        >
          <Box>
            <Typography 
              variant="body1" 
              sx={{ 
                ...(theme.typography?.body1 ?? {}), 
                color: theme.palette.custom?.text?.heading 
              }}
            >
              {displayName}
            </Typography>
            <Typography 
              variant="caption" 
              noWrap
              sx={{ 
                ...(theme.typography?.custom?.paragraph ?? {}), 
                color: theme.palette.custom?.text?.subtext,
                maxWidth: '220px',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayEmail}
            </Typography>
          </Box>

          <Box component="span" sx={{ flex: '1 1 auto' }} />
          
          <Avatar sx={{ backgroundColor: 'rgba(70, 168, 220, 0.16)', height: 46, width: 46 }}>
            <SettingsOutlined sx={{ color: theme.palette.custom?.icon?.primary }} />
          </Avatar>
        </MenuItem>

        <Divider />

        {/* Menu Items */}
        {(MENU_ITEMS ?? []).map((item, index) => {
          const disabled = isItemDisabled(item?.id) === true;
          const menuItemsLength = MENU_ITEMS?.length ?? 0;

          return (
            <React.Fragment key={item?.id ?? index}>
              <MenuItem 
                onClick={() => handleMenuItemClick(item?.id)}
                disabled={disabled}
                sx={{ 
                  ...(theme.typography?.custom?.paragraph ?? {}), 
                  color: item?.color === 'error'
                    ? theme.palette?.error?.main
                    : theme.palette.custom?.text?.heading, 
                  gap: 1,
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                <ListItemIcon>
                  <Avatar 
                    sx={{ 
                      backgroundColor: getAvatarBackgroundColor(item?.color),
                      height: 36, 
                      width: 36,
                      color: getIconColor(item?.color),
                    }}
                  >
                    {item?.icon}
                  </Avatar>
                </ListItemIcon>
                {item?.label}
                
                <Box component="span" sx={{ flex: '1 1 auto' }} />

                {item?.id !== 'logout' && (
                  <KeyboardArrowRightOutlined 
                    sx={{ 
                      color: item?.id === 'help' 
                        ? theme.palette.custom?.icon?.active 
                        : theme.palette.custom?.stroke?.outlineActive 
                    }} 
                  />
                )}
              </MenuItem>
              
              {index < menuItemsLength - 1 && (
                <Divider sx={{ my: 0 }} />
              )}
            </React.Fragment>
          );
        })}
      </Menu>

      {/* Logout Confirmation Modal */}
      <Modal
        open={showLogoutConfirm === true}
        onClose={handleCancelLogout}
        disableBackdropClick={isLoggingOut === true}
        disableEscapeKeyDown={isLoggingOut === true}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.custom?.background?.popup ?? '#13172A',
            borderRadius: theme.spacing?.(1.5) ?? 12,
            padding: { xs: theme.spacing?.(4) ?? 32, md: theme.spacing?.(6) ?? 48 },
            width: { xs: '100%', sm: 400, md: 450 },
            maxWidth: '450px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            outline: 'none',
          }}
        >
          {/* Logout Icon - Circular with red outline */}
          <Box
            sx={{
              width: { xs: theme.spacing?.(6) ?? 48, md: theme.spacing?.(7.5) ?? 60 },
              height: { xs: theme.spacing?.(6) ?? 48, md: theme.spacing?.(7.5) ?? 60 },
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: `0 auto ${theme.spacing?.(3) ?? 24}px auto`,
              backgroundColor: alpha(theme.palette?.error?.main ?? '#EB5456', 0.15),
              '& .MuiSvgIcon-root': {
                fontSize: {
                  xs: typography?.fontSize?.h4 ?? '1.25rem',
                  md: typography?.fontSize?.h2 ?? '1.75rem',
                },
                color: theme.palette?.error?.main,
              },
            }}
          >
            <LogoutOutlined />
          </Box>

          {/* Title */}
          <Typography
            sx={{
              color: theme.palette.custom?.text?.heading ?? theme.palette?.common?.white,
              fontSize: {
                xs: typography?.fontSize?.body1 ?? '1rem',
                md: typography?.fontSize?.subheading1 ?? '1.125rem',
              },
              fontWeight: typography?.fontWeight?.semibold ?? 600,
              textAlign: 'center',
              marginBottom: theme.spacing?.(1.5) ?? 12,
              lineHeight: '1.2',
            }}
          >
            Log Out
          </Typography>

          {/* Message */}
          <Typography
            sx={{
              color: theme.palette.custom?.text?.paragraph ?? '#B0B0B0',
              fontSize: {
                xs: typography?.fontSize?.helperText ?? '0.75rem',
                md: typography?.fontSize?.label ?? '0.875rem',
              },
              textAlign: 'center',
              marginBottom: { xs: theme.spacing?.(3) ?? 24, md: theme.spacing?.(4) ?? 32 },
              lineHeight: '1.4',
            }}
          >
            Are you sure you want to log out?
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: theme.spacing?.(1) ?? 8, md: theme.spacing?.(1.25) ?? 10 },
              justifyContent: 'center',
            }}
          >
            {/* Yes, Confirm Button */}
            <Button
              onClick={handleConfirmLogout}
              disabled={isLoggingOut === true}
              variant="contained"
              sx={{
                backgroundColor: theme.palette?.primary?.main,
                color: theme.palette.custom?.text?.buttonOnPrimary ?? theme.palette?.common?.white,
                borderRadius: theme.spacing?.(1) ?? 8,
                padding: {
                  xs: `${theme.spacing?.(1) ?? 8}px ${theme.spacing?.(1.75) ?? 14}px`,
                  md: `${theme.spacing?.(1.25) ?? 10}px ${theme.spacing?.(2.25) ?? 18}px`,
                },
                fontSize: {
                  xs: typography?.fontSize?.helperText ?? '0.75rem',
                  md: typography?.fontSize?.label ?? '0.875rem',
                },
                fontWeight: typography?.fontWeight?.medium ?? 500,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                minWidth: { xs: theme.spacing?.(15) ?? 120, md: theme.spacing?.(22.5) ?? 180 },
                width: { xs: 'auto', md: 'auto' },
                flex: 1,
                maxWidth: 180,
                '&:hover': {
                  backgroundColor: alpha(theme.palette?.primary?.main ?? '#1976d2', 0.9),
                },
                '&:disabled': {
                  backgroundColor: alpha(theme.palette?.primary?.main ?? '#1976d2', 0.5),
                  color: alpha(theme.palette?.common?.white ?? '#fff', 0.5),
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              {isLoggingOut === true && (
                <CircularProgress 
                  size={16} 
                  sx={{
                    color:
                      theme.palette.custom?.text?.buttonOnPrimary ??
                      theme.palette?.common?.white,
                  }} 
                />
              )}
              Yes, Confirm
            </Button>

            {/* No, Cancel Button */}
            <Button
              onClick={handleCancelLogout}
              disabled={isLoggingOut === true}
              variant="outlined"
              sx={{
                backgroundColor: 'transparent',
                border: `1px solid ${theme.palette?.common?.white ?? '#fff'}`,
                color: theme.palette.custom?.text?.heading ?? theme.palette?.common?.white,
                borderRadius: theme.spacing?.(1) ?? 8,
                padding: {
                  xs: `${theme.spacing?.(1) ?? 8}px ${theme.spacing?.(1.75) ?? 14}px`,
                  md: `${theme.spacing?.(1.25) ?? 10}px ${theme.spacing?.(2.25) ?? 18}px`,
                },
                fontSize: {
                  xs: typography?.fontSize?.helperText ?? '0.75rem',
                  md: typography?.fontSize?.label ?? '0.875rem',
                },
                fontWeight: typography?.fontWeight?.medium ?? 500,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                minWidth: { xs: theme.spacing?.(15) ?? 120, md: theme.spacing?.(22.5) ?? 180 },
                width: { xs: 'auto', md: 'auto' },
                flex: 1,
                maxWidth: 180,
                '&:hover': {
                  backgroundColor: alpha(theme.palette?.common?.white ?? '#fff', 0.1),
                },
                '&:disabled': {
                  borderColor: alpha(theme.palette?.common?.white ?? '#fff', 0.2),
                  color: alpha(theme.palette?.common?.white ?? '#fff', 0.5),
                },
              }}
            >
              No, Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
});

ProfileMenuComponent.propTypes = {
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMenuItemClick: PropTypes.func,
  restrictedMenu: PropTypes.bool,
};

ProfileMenuComponent.defaultProps = {
  userName: 'User',
  userEmail: '',
  restrictedMenu: false,
};

ProfileMenuComponent.displayName = 'ProfileMenuComponent';

export default ProfileMenuComponent;
